/**
 * §13.1.3 Settings — the full tree, reachable from title and pause. Every change
 * applies live and persists immediately (A.6). Every spine §14 line item sits at
 * most three levels deep: Settings → group → control.
 *
 * §13.4 coverage lives here: remap table, dead zones, text scale, colourblind
 * presets, reduced flashes / particles / shake, bloom slider, the AMD-3 post
 * sliders, three volume sliders, and pause-on-focus-loss.
 */

import { button, div, field, panel, panelBody, segmented, setSegmented, setText, slider, span, toggle } from '../dom';
import { formatPercent } from '../format';
import {
  BINDABLE_ACTIONS,
  codeLabel,
  SettingsStore,
  TARGETING_POLICIES,
  TEXT_SCALES,
  UNBOUND,
} from '../settings-store';
import type { Settings } from '../types';
import { Screen, type ActionSink } from './base';

type ApplyFn = (s: Settings) => void;

interface Group {
  root: HTMLElement;
  body: HTMLElement;
}

export class SettingsScreen extends Screen {
  private store: SettingsStore;
  private apply: ApplyFn;
  private returnTo: 'title' | 'pause' = 'title';
  private remapRows: { action_id: string; primary: HTMLButtonElement; alt: HTMLButtonElement }[] = [];
  private capturing: { action_id: string; slot: 'primary' | 'alt'; btn: HTMLButtonElement } | null = null;
  /*
   * Every segmented control is held, because every one has to be a nav stop.
   *
   * `nav.setStops()` is the only way a keyboard or gamepad reaches anything: `src/ui/nav.ts`
   * drives that list and paints `.nav-focus` on the current entry. Only two of these groups
   * were ever registered, so window mode, damage numbers, the whole key-binding table and the
   * targeting policy were **pointer-only** — 44 controls on this screen unreachable without a
   * mouse or a finger. Found by sweeping every interactive element against the registered
   * stop list; nothing else could see it, because each of those buttons is the right size,
   * the right colour and perfectly legible.
   *
   * A group is one stop, matching the two that were already registered: the segmented control
   * handles its own left/right internally, so registering its buttons individually would turn
   * this screen into fifty stops to traverse.
   */
  private windowModeGroup: HTMLElement;
  private textScaleGroup: HTMLElement;
  private colorblindGroup: HTMLElement;

  constructor(emit: ActionSink, store: SettingsStore, apply: ApplyFn) {
    super('settings', emit);
    this.store = store;
    this.apply = apply;
    const s = store.value;

    const head = div('screen-head');
    head.appendChild(div('screen-title', 'SETTINGS'));
    const back = button('Back', 'ghost', 'angle-left');
    back.addEventListener('click', () => this.close());
    head.appendChild(back);
    this.root.appendChild(head);

    const scroll = div('settings-scroll');
    this.root.appendChild(scroll);

    /*
     * A jump bar, because this screen is addressed by jumping.
     *
     * Cycle 010 settled the brief — "somewhere you go to change ONE control you already have
     * in mind" — and won the desktop by putting four of five group headings on screen at once,
     * so a player could aim by spatial location. On a phone the same screen is **3203 px in
     * an 844 px viewport with 2 of 5 headings visible**, and Accessibility is 2621 px down.
     * Scrolling three screens to find a control you already named is the exact task 010 said
     * this surface exists to make fast.
     *
     * The bar names all five groups in the space of one row and takes you to any of them in a
     * tap. It is built from the same list the groups are, so it cannot drift out of step.
     */
    this.root.insertBefore(this.navBar, scroll);

    // --- Audio (§12.5) ---
    const audio = this.group('Audio', 'sound-on');
    const master = slider('Master volume', s.audio.master, 0, 1, 0.01, formatPercent, (v) =>
      this.write((x) => (x.audio.master = v)),
    );
    const music = slider('Music volume', s.audio.music, 0, 1, 0.01, formatPercent, (v) =>
      this.write((x) => (x.audio.music = v)),
    );
    const sfx = slider('SFX volume', s.audio.sfx, 0, 1, 0.01, formatPercent, (v) =>
      this.write((x) => (x.audio.sfx = v)),
    );
    audio.body.appendChild(master.root);
    audio.body.appendChild(music.root);
    audio.body.appendChild(sfx.root);
    scroll.appendChild(audio.root);
    this.addNavStop('Audio', audio.root);

    // --- Video (§11.1 + AMD-3) ---
    const video = this.group('Video', 'eye');
    const bloom = slider('Bloom intensity', s.video.bloom_intensity, 0, 1, 0.01, formatPercent, (v) =>
      this.write((x) => (x.video.bloom_intensity = v)),
    );
    const scan = slider('Scanlines', s.video.scanline_intensity, 0, 0.35, 0.01, (v) => formatPercent(v / 0.35), (v) =>
      this.write((x) => (x.video.scanline_intensity = v)),
    );
    const vhs = slider('VHS warp', s.video.vhs_intensity, 0, 1, 0.01, formatPercent, (v) =>
      this.write((x) => (x.video.vhs_intensity = v)),
    );
    video.body.appendChild(bloom.root);
    video.body.appendChild(scan.root);
    video.body.appendChild(vhs.root);
    this.windowModeGroup = segmented(
      [
        { id: 'windowed', label: 'Windowed' },
        { id: 'fullscreen', label: 'Fullscreen' },
      ],
      s.video.window_mode,
      (id) => this.write((x) => (x.video.window_mode = id === 'fullscreen' ? 'fullscreen' : 'windowed')),
    );
    video.body.appendChild(field('Window mode', this.windowModeGroup));
    video.body.appendChild(toggle('Damage numbers', s.video.damage_numbers, (v) => this.write((x) => (x.video.damage_numbers = v))));
    video.body.appendChild(
      field(
        'Performance tier',
        segmented(
          [
            { id: 'auto', label: 'Auto' },
            { id: 'handheld', label: 'Low' },
            { id: 'standard', label: 'Mid' },
            { id: 'desktop', label: 'High' },
          ],
          s.video.perf_tier,
          (id) =>
            this.write((x) => {
              if (id === 'auto' || id === 'desktop' || id === 'standard' || id === 'handheld') x.video.perf_tier = id;
            }),
        ),
        'Auto probes the device and holds 60 FPS by thinning density, never rules.',
      ),
    );
    scroll.appendChild(video.root);
    this.addNavStop('Video', video.root);

    // --- Controls (§3.9) ---
    const controls = this.group('Controls', 'command');
    const remapTable = div('remap-table');
    for (let i = 0; i < BINDABLE_ACTIONS.length; i++) {
      const a = BINDABLE_ACTIONS[i];
      const row = div('remap-row');
      const label = div('remap-label', a.label);
      if (a.reserved) label.appendChild(span('remap-reserved', 'reserved'));
      row.appendChild(label);
      const bound = s.input.bindings[a.id];
      const primary = button(codeLabel(bound ? bound.primary : UNBOUND), 'bind');
      const alt = button(codeLabel(bound ? bound.alt : UNBOUND), 'bind');
      if (a.reserved) {
        primary.disabled = true;
        primary.classList.add('is-locked');
      } else {
        primary.addEventListener('click', () => this.beginCapture(a.id, 'primary', primary));
      }
      alt.addEventListener('click', () => this.beginCapture(a.id, 'alt', alt));
      row.appendChild(primary);
      row.appendChild(alt);
      const reset = button('Reset', 'ghost small');
      reset.addEventListener('click', () => this.resetAction(a.id));
      row.appendChild(reset);
      remapTable.appendChild(row);
      this.remapRows.push({ action_id: a.id, primary, alt });
    }
    controls.body.appendChild(field('Remap', remapTable, 'Assigning a used input steals it; the old owner shows Unbound.'));

    const inner = slider('Stick dead zone (inner)', s.input.gamepad_deadzone.inner, 0.1, 0.3, 0.01, (v) => v.toFixed(2), (v) =>
      this.write((x) => (x.input.gamepad_deadzone.inner = v)),
    );
    const outer = slider('Stick dead zone (outer)', s.input.gamepad_deadzone.outer, 0.8, 1.0, 0.01, (v) => v.toFixed(2), (v) =>
      this.write((x) => (x.input.gamepad_deadzone.outer = v)),
    );
    controls.body.appendChild(inner.root);
    controls.body.appendChild(outer.root);
    const restore = button('Restore default bindings', 'ghost', 'refresh');
    restore.addEventListener('click', () => {
      this.store.restoreBindings();
      this.refreshBindings();
      this.apply(this.store.value);
      this.emit({ type: 'restore_bindings' });
    });
    controls.body.appendChild(restore);
    scroll.appendChild(controls.root);
    this.addNavStop('Controls', controls.root);

    // --- Gameplay ---
    const gameplay = this.group('Gameplay', 'shapes');
    gameplay.body.appendChild(
      field(
        'Default targeting policy',
        segmented(
          TARGETING_POLICIES.map((p) => ({ id: p.id, label: p.label })),
          s.targeting_policy_default,
          (id) => this.write((x) => (x.targeting_policy_default = id)),
        ),
      ),
    );
    gameplay.body.appendChild(
      toggle('Pause on focus loss', s.access.pause_on_focus_loss, (v) => this.write((x) => (x.access.pause_on_focus_loss = v))),
    );
    const replay = button('Replay onboarding', 'ghost', 'refresh');
    replay.addEventListener('click', () => {
      this.write((x) => (x.gameplay.onboarding_seen = false));
      this.emit({ type: 'replay_onboarding' });
    });
    gameplay.body.appendChild(replay);
    scroll.appendChild(gameplay.root);
    this.addNavStop('Gameplay', gameplay.root);

    // --- Accessibility (§13.4) ---
    const access = this.group('Accessibility', 'badge-check');
    this.textScaleGroup = segmented(
      TEXT_SCALES.map((t) => ({ id: String(t), label: Math.round(t * 100) + '%' })),
      String(s.video.text_scale),
      (id) => this.write((x) => (x.video.text_scale = Number(id))),
    );
    access.body.appendChild(field('Text scale', this.textScaleGroup));

    this.colorblindGroup = segmented(
      [
        { id: 'off', label: 'Off' },
        { id: 'deuteranopia', label: 'Deuter' },
        { id: 'protanopia', label: 'Protan' },
        { id: 'tritanopia', label: 'Tritan' },
      ],
      s.video.colorblind_mode,
      (id) =>
        this.write((x) => {
          if (id === 'off' || id === 'deuteranopia' || id === 'protanopia' || id === 'tritanopia') x.video.colorblind_mode = id;
        }),
    );
    access.body.appendChild(field('Colourblind preset', this.colorblindGroup, 'Shape and motion codes never change; only accent hues remap.'));

    access.body.appendChild(toggle('Reduced flashes', s.video.reduced_flashes, (v) => this.write((x) => (x.video.reduced_flashes = v))));
    access.body.appendChild(toggle('Reduced particles', s.video.reduced_particles, (v) => this.write((x) => (x.video.reduced_particles = v))));
    access.body.appendChild(toggle('Reduced screen shake', s.video.reduced_shake, (v) => this.write((x) => (x.video.reduced_shake = v))));
    access.body.appendChild(
      div('photosafety', 'Default settings never show more than 3 full-screen flashes per second.'),
    );
    scroll.appendChild(access.root);
    this.addNavStop('Accessibility', access.root);

    const restoreAll = button('Restore all defaults', 'ghost', 'refresh');
    restoreAll.addEventListener('click', () => {
      this.store.restoreDefaults();
      this.refreshAll();
      this.apply(this.store.value);
      this.emit({ type: 'settings_changed', settings: this.store.value });
    });
    scroll.appendChild(restoreAll);

    this.stops = [
      master.input,
      music.input,
      sfx.input,
      bloom.input,
      scan.input,
      vhs.input,
      inner.input,
      outer.input,
      restore,
      replay,
      this.windowModeGroup,
      this.textScaleGroup,
      this.colorblindGroup,
      restoreAll,
      back,
    ];

    /*
     * Anything else focusable gets picked up from the DOM, in document order.
     *
     * The hand-written list above is why 44 controls on this screen were unreachable by
     * keyboard or gamepad: every segmented control, every key binding and the whole targeting
     * policy had to be remembered, and they were not. A list that must be maintained in step
     * with the markup will fall out of step with the markup.
     *
     * A `.segmented` is ONE stop, matching the groups that were already registered — the
     * control handles its own left/right internally, and registering its buttons individually
     * would make this screen fifty stops deep. Everything explicit above keeps its position;
     * the sweep only adds what the list forgot.
     */
    const extra: HTMLElement[] = [];
    for (const el of scroll.querySelectorAll<HTMLElement>('.segmented, .btn')) {
      if (this.stops.includes(el)) continue;
      if (this.stops.some((s) => s.contains(el))) continue;
      if (el.closest('.segmented') !== null && !el.classList.contains('segmented')) continue;
      if (extra.some((e) => e.contains(el))) continue;
      extra.push(el);
    }
    // The jump bar is navigation, not content, so it leads.
    this.stops = [...this.navBar.querySelectorAll<HTMLElement>('.btn'), ...this.stops, ...extra];
  }

  /** AMD-1: a jump target is a touch target, so these are real buttons at the 44 px floor. */
  private addNavStop(title: string, target: HTMLElement): void {
    const b = button(title, 'settings-nav-stop');
    b.addEventListener('click', () => {
      target.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
    this.navBar.appendChild(b);
  }

  private readonly navBar: HTMLElement = div('settings-nav');

  private group(title: string, iconName: string): Group {
    const p = panel('settings-group');
    const body = panelBody(p);
    const h = div('group-head');
    h.appendChild(div('group-title', title));
    body.appendChild(h);
    const inner = div('group-body');
    body.appendChild(inner);
    p.dataset.icon = iconName;
    return { root: p, body: inner };
  }

  private write(mutate: (s: Settings) => void): void {
    const next = this.store.update(mutate);
    this.apply(next);
    this.emit({ type: 'settings_changed', settings: next });
  }

  /** §3.9 capture: the next key / mouse button / pad button becomes the binding. */
  private beginCapture(actionId: string, slot: 'primary' | 'alt', btn: HTMLButtonElement): void {
    this.cancelCapture();
    this.capturing = { action_id: actionId, slot, btn };
    btn.classList.add('is-capturing');
    btn.querySelector('.btn-label')!.textContent = 'Press…';
    this.emit({ type: 'begin_remap', action_id: actionId, slot });
  }

  private cancelCapture(): void {
    if (!this.capturing) return;
    this.capturing.btn.classList.remove('is-capturing');
    this.capturing = null;
    this.refreshBindings();
  }

  /** Returns true when the event was consumed as a rebind. */
  captureKey(e: KeyboardEvent): boolean {
    if (!this.capturing) return false;
    e.preventDefault();
    const code = e.code === 'Escape' ? UNBOUND : e.code;
    const cap = this.capturing;
    this.capturing = null;
    cap.btn.classList.remove('is-capturing');
    this.store.bind(cap.action_id, cap.slot, code);
    this.refreshBindings();
    this.apply(this.store.value);
    this.emit({ type: 'settings_changed', settings: this.store.value });
    return true;
  }

  get isCapturing(): boolean {
    return this.capturing !== null;
  }

  private resetAction(actionId: string): void {
    for (let i = 0; i < BINDABLE_ACTIONS.length; i++) {
      const a = BINDABLE_ACTIONS[i];
      if (a.id !== actionId) continue;
      this.store.bind(actionId, 'primary', a.primary);
      this.store.bind(actionId, 'alt', a.alt);
      break;
    }
    this.refreshBindings();
    this.apply(this.store.value);
  }

  private refreshBindings(): void {
    const b = this.store.value.input.bindings;
    for (let i = 0; i < this.remapRows.length; i++) {
      const row = this.remapRows[i];
      const bound = b[row.action_id];
      setText(row.primary.querySelector('.btn-label')!, codeLabel(bound ? bound.primary : UNBOUND));
      setText(row.alt.querySelector('.btn-label')!, codeLabel(bound ? bound.alt : UNBOUND));
      row.primary.classList.toggle('is-unbound', !bound || bound.primary === UNBOUND);
      row.alt.classList.toggle('is-unbound', !bound || bound.alt === UNBOUND);
    }
  }

  private refreshAll(): void {
    const s = this.store.value;
    this.refreshBindings();
    setSegmented(this.textScaleGroup, String(s.video.text_scale));
    setSegmented(this.colorblindGroup, s.video.colorblind_mode);
  }

  setReturnTo(screen: 'title' | 'pause'): void {
    this.returnTo = screen;
  }

  private close(): void {
    this.cancelCapture();
    this.emit({ type: 'goto', screen: this.returnTo });
  }

  override onShow(): void {
    this.refreshAll();
  }

  override onBack(): boolean {
    if (this.capturing) {
      this.cancelCapture();
      return true;
    }
    this.close();
    return true;
  }
}
